import { useEffect, useId, useState } from 'react'
import { Icon } from '@iconify/react'
import { formatShareExpiresOn } from '@/lib/managerial-dashboard-access'
import { managerialIsoDate } from '@/lib/managerial-calendar'
import { useManagerialDashboardShares } from '../../hooks/useManagerialDashboardShares'
import { InstitutionPicker } from './InstitutionPicker'
import styles from './ShareAccessModal.module.css'

type ShareAccessModalProps = {
  isOpen: boolean
  onClose: () => void
}

export function ShareAccessModal({ isOpen, onClose }: ShareAccessModalProps) {
  const titleId = useId()
  const shares = useManagerialDashboardShares(isOpen)
  const [selectedInstitutionId, setSelectedInstitutionId] = useState('')
  const [expiresOn, setExpiresOn] = useState(managerialIsoDate)
  const [rowExpiresOn, setRowExpiresOn] = useState<Record<number, string>>({})
  const [pendingRevokeId, setPendingRevokeId] = useState<number | null>(null)

  useEffect(() => {
    if (!isOpen) return
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [isOpen])

  if (!isOpen) return null

  const canGrant =
    selectedInstitutionId !== '' &&
    expiresOn !== '' &&
    !shares.isSaving &&
    !shares.isLoading

  return (
    <div
      className={styles.overlay}
      role="presentation"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose()
        }
      }}
    >
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <header className={styles.header}>
          <div>
            <h2 id={titleId} className={styles.title}>
              Compartilhar Dash Gerencial
            </h2>
            <p className={styles.subtitle}>
              Escolha a conta da instituição e até quando ela poderá ver o painel
              completo.
            </p>
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Fechar"
          >
            <Icon icon="mdi:close" />
          </button>
        </header>

        <div className={styles.body}>
          <form
            className={styles.form}
            onSubmit={(event) => {
              event.preventDefault()
              const institutionId = Number(selectedInstitutionId)
              if (!Number.isInteger(institutionId) || institutionId <= 0) return
              void shares.grantAccess(institutionId, expiresOn).then((ok) => {
                if (ok) {
                  setSelectedInstitutionId('')
                  shares.setSearch('')
                }
              })
            }}
          >
            <InstitutionPicker
              search={shares.search}
              onSearchChange={shares.setSearch}
              candidates={shares.candidates}
              selectedId={selectedInstitutionId}
              onSelect={setSelectedInstitutionId}
              isSearching={shares.isSearching}
              disabled={shares.isSaving}
            />

            <div className={styles.grantRow}>
              <label className={styles.field} htmlFor="share-expires-on">
                Válido até
                <input
                  id="share-expires-on"
                  className={styles.input}
                  type="date"
                  value={expiresOn}
                  min={managerialIsoDate()}
                  onChange={(event) => setExpiresOn(event.target.value)}
                />
              </label>
              <button type="submit" className={styles.primaryButton} disabled={!canGrant}>
                {shares.isSaving ? 'Salvando...' : 'Conceder acesso'}
              </button>
            </div>
          </form>

          {shares.error ? (
            <p className={`${styles.status} ${styles.error}`} role="alert">
              {shares.error}
            </p>
          ) : null}
          {shares.success ? (
            <p className={`${styles.status} ${styles.success}`} role="status">
              {shares.success}
            </p>
          ) : null}

          <section className={styles.shareListSection} aria-label="Acessos vigentes">
            <h3 className={styles.listTitle}>Acessos vigentes</h3>
            {shares.isLoading ? (
              <p className={styles.empty}>Carregando acessos...</p>
            ) : shares.shares.length === 0 ? (
              <p className={styles.empty}>Nenhuma instituição com acesso no momento.</p>
            ) : (
              <ul className={styles.list}>
                {shares.shares.map((share) => {
                  const currentExpiry = rowExpiresOn[share.id] ?? share.expires_on
                  return (
                    <li key={share.id} className={styles.item}>
                      <div className={styles.itemMeta}>
                        <span className={styles.itemName}>{share.institution_name}</span>
                        <span className={styles.itemEmail}>
                          {share.institution_email}
                        </span>
                        <span className={styles.itemExpiry}>
                          Válido até {formatShareExpiresOn(share.expires_on)}
                        </span>
                      </div>
                      <div className={styles.itemActions}>
                        <label
                          className={styles.field}
                          htmlFor={`share-row-expires-${share.id}`}
                        >
                          Novo prazo
                          <input
                            id={`share-row-expires-${share.id}`}
                            className={styles.input}
                            type="date"
                            value={currentExpiry}
                            min={managerialIsoDate()}
                            onChange={(event) => {
                              setRowExpiresOn((current) => ({
                                ...current,
                                [share.id]: event.target.value,
                              }))
                            }}
                          />
                        </label>
                        <button
                          type="button"
                          className={styles.secondaryButton}
                          disabled={shares.isSaving || currentExpiry === share.expires_on}
                          onClick={() => {
                            void shares.updateExpiry(share.id, currentExpiry)
                          }}
                        >
                          {shares.isSaving ? 'Salvando...' : 'Alterar prazo'}
                        </button>
                        {pendingRevokeId === share.id ? (
                          <button
                            type="button"
                            className={styles.dangerButton}
                            disabled={shares.isSaving}
                            onClick={() => {
                              void shares.revokeAccess(share.id).then((ok) => {
                                if (ok) setPendingRevokeId(null)
                              })
                            }}
                          >
                            {shares.isSaving ? 'Revogando...' : 'Confirmar revogação'}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className={styles.secondaryButton}
                            disabled={shares.isSaving}
                            onClick={() => setPendingRevokeId(share.id)}
                          >
                            Revogar
                          </button>
                        )}
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </section>
        </div>
      </div>
    </div>
  )
}
